// Diagnostics: persistent error log + capability probe.
//
// Motivation: mobile users saw processing fail with no copyable error.
// iOS Safari / Firefox throw their real errors somewhere the previous UI
// never showed (model-init failures were silently swallowed by the
// generic rewrap in lib/process.ts; the original message was lost
// before it could reach the user). This module captures everything
// into localStorage so errors survive page reloads and the "worked
// first, failed second" sequence, and provides a plain-text blob
// suitable for copying off an iPhone and pasting into a bug report.

const STORAGE_KEY = "privatebg:diag:log:v1";
const MAX_ENTRIES = 20;

export interface DiagEntry {
  // ISO timestamp.
  t: string;
  // Caller-provided stage label, e.g. "initializeModel", "processImage", "iOS-runtime".
  stage: string;
  // Error name (e.g. "TypeError", "RangeError", "AbortError").
  name: string;
  // Error message.
  message: string;
  // Full stack if available.
  stack?: string;
  // .cause chain, if any (each level serialised).
  causeChain?: Array<{ name: string; message: string; stack?: string }>;
  // Arbitrary extra context (model id, wasm.proxy setting, image size, ...).
  context?: Record<string, unknown>;
}

function readLog(): DiagEntry[] {
  if (typeof localStorage === "undefined") return [];
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function writeLog(entries: DiagEntry[]): void {
  if (typeof localStorage === "undefined") return;
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(entries.slice(-MAX_ENTRIES)));
  } catch {
    // Storage full or blocked (private mode on Safari can throw) — ignore.
  }
}

function serialiseCauseChain(err: unknown): DiagEntry["causeChain"] {
  const chain: DiagEntry["causeChain"] = [];
  let current: unknown = err;
  // Cap at 5 to prevent pathological cycles.
  for (let i = 0; i < 5; i++) {
    if (!(current instanceof Error)) break;
    // Skip the first error — it's already in the top-level entry.
    if (i > 0) {
      chain.push({
        name: current.name,
        message: current.message,
        stack: current.stack,
      });
    }
    current = (current as Error & { cause?: unknown }).cause;
    if (current === undefined || current === null) break;
  }
  return chain.length > 0 ? chain : undefined;
}

export function logDiagnostic(
  stage: string,
  err: unknown,
  context?: Record<string, unknown>,
): void {
  const entry: DiagEntry = {
    t: new Date().toISOString(),
    stage,
    name: err instanceof Error ? err.name : "NonError",
    message: err instanceof Error ? err.message : String(err),
    stack: err instanceof Error ? err.stack : undefined,
    causeChain: serialiseCauseChain(err),
    context,
  };
  const log = readLog();
  log.push(entry);
  writeLog(log);
}

export function getDiagnosticLog(): DiagEntry[] {
  return readLog();
}

export function clearDiagnosticLog(): void {
  if (typeof localStorage === "undefined") return;
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
}

export interface CapabilityFlags {
  userAgent: string;
  platform: string;
  maxTouchPoints: number;
  webGPU: boolean;
  sharedArrayBuffer: boolean;
  crossOriginIsolated: boolean;
  webAssembly: boolean;
  deviceMemoryGB?: number;
  hardwareConcurrency?: number;
  storageQuotaMB?: number;
  storageUsageMB?: number;
  onLine: boolean;
  language: string;
  timezone: string;
  cookieEnabled: boolean;
  serviceWorker: boolean;
}

export async function probeCapabilities(): Promise<CapabilityFlags> {
  const nav = typeof navigator !== "undefined" ? navigator : ({} as Navigator);
  const flags: CapabilityFlags = {
    userAgent: nav.userAgent ?? "",
    platform: nav.platform ?? "",
    maxTouchPoints: nav.maxTouchPoints ?? 0,
    webGPU: typeof (nav as Navigator & { gpu?: unknown }).gpu !== "undefined",
    sharedArrayBuffer: typeof SharedArrayBuffer !== "undefined",
    crossOriginIsolated:
      typeof (globalThis as { crossOriginIsolated?: boolean })
        .crossOriginIsolated === "boolean"
        ? (globalThis as { crossOriginIsolated?: boolean }).crossOriginIsolated === true
        : false,
    webAssembly: typeof WebAssembly !== "undefined",
    deviceMemoryGB: (nav as Navigator & { deviceMemory?: number }).deviceMemory,
    hardwareConcurrency: nav.hardwareConcurrency,
    onLine: nav.onLine ?? true,
    language: nav.language ?? "",
    timezone:
      typeof Intl !== "undefined" && Intl.DateTimeFormat
        ? Intl.DateTimeFormat().resolvedOptions().timeZone
        : "",
    cookieEnabled: nav.cookieEnabled ?? false,
    serviceWorker: typeof nav.serviceWorker !== "undefined",
  };

  if (nav.storage && typeof nav.storage.estimate === "function") {
    try {
      const est = await nav.storage.estimate();
      if (typeof est.quota === "number") {
        flags.storageQuotaMB = Math.round(est.quota / (1024 * 1024));
      }
      if (typeof est.usage === "number") {
        flags.storageUsageMB = Math.round(est.usage / (1024 * 1024));
      }
    } catch {
      // ignore
    }
  }

  return flags;
}

export async function buildDiagnosticReport(): Promise<string> {
  const caps = await probeCapabilities();
  const log = getDiagnosticLog();

  const lines: string[] = [];
  lines.push("PrivateBG diagnostics report");
  lines.push("Generated: " + new Date().toISOString());
  lines.push("URL: " + (typeof location !== "undefined" ? location.href : "n/a"));
  lines.push("");
  lines.push("=== Capabilities ===");
  lines.push(JSON.stringify(caps, null, 2));
  lines.push("");
  lines.push("=== Error log (" + log.length + " entries, newest last) ===");
  if (log.length === 0) {
    lines.push("(no errors logged)");
  } else {
    for (const entry of log) {
      lines.push("");
      lines.push("--- " + entry.t + " | " + entry.stage + " ---");
      lines.push(entry.name + ": " + entry.message);
      if (entry.context) {
        lines.push("context: " + JSON.stringify(entry.context));
      }
      if (entry.stack) {
        lines.push("stack:");
        lines.push(entry.stack);
      }
      if (entry.causeChain && entry.causeChain.length > 0) {
        for (let i = 0; i < entry.causeChain.length; i++) {
          const c = entry.causeChain[i];
          lines.push("cause[" + i + "]: " + c.name + ": " + c.message);
          if (c.stack) lines.push(c.stack);
        }
      }
    }
  }
  lines.push("");
  lines.push("=== End of report ===");
  return lines.join("\n");
}
